/**
 * Blocks a CLIENT from reaching any form until they have accepted the current declaration set.
 *
 * Fails CLOSED: while acceptance is unknown, or if the lookup errors, the client does not get
 * through. An unproven acceptance is not an acceptance.
 *
 * Staff pass straight through: they are not a client and make no declaration.
 *
 * Sponsors ARE gated, but on their OWN undertaking. A partner sponsor (40SP) is committing to the
 * applicant's financial obligations to the Commonwealth and to 2 years of support and accommodation;
 * a visitor sponsor (1149) is committing to a security bond that can be forfeited. Those are
 * different instruments from each other and from the applicant's truthfulness attestation, so the
 * audience decides the set. Showing anyone the wrong one would record a promise they never made.
 *
 * This gates on `actuallyStaff`, NOT `isStaff`. `isStaff` is impersonation-aware and goes false
 * while a staff member previews a client's view — if we gated on that, previewing would show the
 * staff member the client's declarations and let them accept on the client's behalf, writing a
 * fabricated attestation. A declaration must only ever be made by the person it belongs to.
 */

import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useMatter } from '../hooks/useMatter';
import { useDeclarations } from '../hooks/useDeclarations';
import { declarationAudienceFor } from '../lib/declarations/audience';

const SETUP_PATH = '/welcome';

export default function DeclarationsGate({ children }: { children: ReactNode }) {
  const { contact, activeMatter, actuallyStaff, loading: matterLoading } = useMatter();

  // WHICH set this person makes: applicant, partner sponsor (40SP), or visitor sponsor (1149).
  // `null` = they make none, and are never gated (staff, witness, or a sponsor on a visa that has
  // no sponsor form). Sponsors ARE gated now, but on THEIR OWN undertaking, never the applicant's.
  //
  // DeclarationsSetup must resolve the audience identically, or someone exempt could still land on
  // /welcome and record an acceptance they were never meant to make.
  const audience = declarationAudienceFor(contact?.contact_type, activeMatter?.visa_subclass);
  const exempt = actuallyStaff || audience === null;

  const { hasAccepted, loading } = useDeclarations(contact?.id ?? null, exempt, audience);
  const location = useLocation();

  // Never bounce the setup page itself, or it would redirect to itself forever.
  if (location.pathname === SETUP_PATH) return <>{children}</>;

  if (matterLoading || loading || hasAccepted === null) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <div className="text-sm text-slate-500">Loading...</div>
      </div>
    );
  }

  if (!hasAccepted) {
    return <Navigate to={SETUP_PATH} replace />;
  }

  return <>{children}</>;
}
